import { ApiError } from '@artic/api-client';
import { Badge } from '@artic/ui/badge';
import { Button } from '@artic/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@artic/ui/card';
import { useFeatureFlag } from '@artic/ui/feature-flag';
import { Skeleton } from '@artic/ui/skeleton';
import { toast } from '@artic/ui/toast';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryState } from 'nuqs';
import { useMemo, useState } from 'react';

import { usePartnerId } from '../../features/inbox/use-partner-name';
import { ProjectPageHeader } from '../../features/project-planning/project-page-header';
import { ProjectStepper } from '../../features/project-planning/project-stepper';
import { releaseGateDecision } from '../../features/project-planning/project-workload-category';
import {
  parseUnconfirmedCategories,
  projectPlanningDetailQueryOptions,
  useReleaseDraftWorkOrders,
  useReReleaseWorkOrders,
  useReleaseProject,
} from '../../features/project-planning/queries';
import {
  ReleaseBlockedModal,
  ReleaseWithoutCategoriesModal,
} from '../../features/project-planning/release-category-gate';
import { resolveReleaseView } from '../../features/project-planning/release-view';
import { projectActionSearchParsers } from '../../features/project-planning/search';

export const Route = createFileRoute('/projects/$projectId_/release')({
  staticData: {
    breadcrumb: (match, queryClient) => {
      const { projectId } = match.params as { projectId: string };
      const data = queryClient.getQueryData(projectPlanningDetailQueryOptions(projectId).queryKey);
      return data ? `${data.project_name} — Release` : 'Project Release';
    },
    layout: 'full-height',
  },
  component: ProjectReleasePage,
});

function ProjectReleasePage() {
  const planner = useFeatureFlag('planner');

  if (!planner) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Feature Not Available</h2>
        <p className="text-muted-foreground text-sm">
          Production Planning is not enabled for your organisation.
        </p>
      </div>
    );
  }

  return <ProjectReleaseContent />;
}

interface MissingRequiredFieldsRow {
  work_order_id: string;
  missing: string[];
}

/**
 * Backend response shape when `releaseProject` rejects with
 * `MISSING_REQUIRED_FIELDS`. Mirrors `BadRequestException` payload from
 * `ProjectPlanningService.releaseProject` (ENG-520).
 */
function parseMissingRequiredFields(err: unknown): MissingRequiredFieldsRow[] | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const body = err.bodyAsRecord;
  if (!body || body['code'] !== 'MISSING_REQUIRED_FIELDS') return null;
  const rows = body['work_orders'];
  if (!Array.isArray(rows)) return null;
  const result: MissingRequiredFieldsRow[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    if (typeof r['work_order_id'] !== 'string' || !Array.isArray(r['missing'])) continue;
    result.push({
      work_order_id: r['work_order_id'],
      missing: r['missing'].filter((m): m is string => typeof m === 'string'),
    });
  }
  return result.length > 0 ? result : null;
}

function ProjectReleaseContent() {
  const { projectId } = Route.useParams();
  const partnerId = usePartnerId();
  const navigate = useNavigate();
  const workloadCategoriesEnabled = useFeatureFlag('workload_categories');
  const [missingFieldsByWo, setMissingFieldsByWo] = useState<MissingRequiredFieldsRow[] | null>(
    null,
  );
  // ENG-608 release gate modal — null | which gate variant is open.
  const [categoryGate, setCategoryGate] = useState<'blocked' | 'zero_confirmed' | null>(null);

  // `?ids=<uuid>,<uuid>` scopes the re-release branch to the planner's
  // current selection; absent means every redrafting WO on the project. The
  // toolbar's "Release N work orders" button populates this; the stepper's
  // "Release" link drops it. Defensively intersected with the project's
  // actual rows so stale URLs don't claim phantom WOs.
  const [selectedIds] = useQueryState('ids', projectActionSearchParsers.ids);

  const {
    data: project,
    isLoading,
    isError,
  } = useQuery(projectPlanningDetailQueryOptions(projectId));

  const release = useReleaseProject(projectId);
  const reRelease = useReReleaseWorkOrders(projectId, partnerId ?? '');
  const releaseDrafts = useReleaseDraftWorkOrders(projectId);

  const workOrderNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const wo of project?.work_orders ?? []) {
      map.set(wo.work_order_id, wo.work_order_name);
    }
    return map;
  }, [project?.work_orders]);

  // All partition + branch-selection logic for the page lives in
  // `resolveReleaseView` (pure + unit-tested). Computed before the
  // loading/error guards so hook order stays stable; a `draft` status stands
  // in while the project loads (the result is unused until `project` resolves).
  const releaseView = useMemo(
    () =>
      resolveReleaseView(
        project?.project_status ?? 'draft',
        project?.work_orders ?? [],
        selectedIds,
        !!partnerId,
      ),
    [project?.project_status, project?.work_orders, selectedIds, partnerId],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[40vh] w-full" />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Error Loading Project</h2>
        <p className="text-muted-foreground text-sm">
          Could not load the project. Please try again.
        </p>
      </div>
    );
  }

  const workOrderCount = project.work_orders.length;
  const {
    redraftingIds,
    draftIds,
    scheduledCount,
    inProgressCount,
    completeCount,
    isSubsetScope,
    isReleased,
    isProjectRedrafting,
    hasRedraftingInScope,
    hasDraftInScope,
    showReReleaseBranch,
    canReRelease,
  } = releaseView;

  function runRelease(workloadCategories?: 'apply_suggested' | 'none') {
    setMissingFieldsByWo(null);
    release.mutate(workloadCategories !== undefined ? { workloadCategories } : {}, {
      onSuccess: () => {
        setCategoryGate(null);
        toast.success('Project released');
        void navigate({ to: '/projects/$projectId', params: { projectId } });
      },
      onError: (err: Error) => {
        const missing = parseMissingRequiredFields(err);
        if (missing) {
          setCategoryGate(null);
          setMissingFieldsByWo(missing);
          toast.error(
            `Cannot release: ${missing.length} work order${missing.length === 1 ? '' : 's'} missing required field${missing.length === 1 ? '' : 's'}`,
          );
          return;
        }
        // Defensive: the backend enforces the workload gate regardless of
        // the client-side decision — surface its block as the modal too.
        if (parseUnconfirmedCategories(err)) {
          setCategoryGate('blocked');
          return;
        }
        toast.error(err.message || 'Failed to release project');
      },
    });
  }

  function handleRelease() {
    // ENG-608 §9 — gate the fresh release on confirmed workload categories.
    const decision = releaseGateDecision(
      project?.product_workload_categories ?? [],
      workloadCategoriesEnabled,
    );
    if (decision === 'block_unconfirmed') {
      setCategoryGate('blocked');
      return;
    }
    if (decision === 'zero_confirmed') {
      setCategoryGate('zero_confirmed');
      return;
    }
    runRelease();
  }

  function handleApplyAllAndRelease() {
    // Server-side atomic: the release transaction confirms every
    // suggestion, then releases — no separate apply round trip.
    runRelease('apply_suggested');
  }

  async function handleReRelease() {
    if (!canReRelease) return;
    setMissingFieldsByWo(null);

    // Release the new draft rows FIRST so a missing-required-field error blocks
    // the whole re-release (including the redrafting rows) with a fix-list —
    // consistent with the initial Release flow.
    if (hasDraftInScope) {
      try {
        await releaseDrafts.mutateAsync();
      } catch (err) {
        const missing = parseMissingRequiredFields(err);
        if (missing) {
          setMissingFieldsByWo(missing);
          toast.error(
            `Cannot re-release: ${missing.length} work order${missing.length === 1 ? '' : 's'} missing required field${missing.length === 1 ? '' : 's'}`,
          );
          return;
        }
        toast.error(err instanceof Error ? err.message : 'Failed to release draft work orders');
        return;
      }
    }

    // Flip the redrafting rows back to scheduled.
    let reReleasedCount = 0;
    if (hasRedraftingInScope) {
      try {
        const result = await reRelease.mutateAsync(redraftingIds);
        reReleasedCount = result.updated.length;
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to re-release work orders');
        return;
      }
    }

    const messages: string[] = [];
    if (reReleasedCount > 0) {
      messages.push(`re-released ${reReleasedCount} work order${reReleasedCount === 1 ? '' : 's'}`);
    }
    if (hasDraftInScope) {
      messages.push(
        `released ${draftIds.length} new work order${draftIds.length === 1 ? '' : 's'}`,
      );
    }
    const summary = messages.join(' and ');
    toast.success(
      summary ? summary.charAt(0).toUpperCase() + summary.slice(1) : 'No work orders changed',
    );
    void navigate({ to: '/projects/$projectId', params: { projectId } });
  }

  return (
    <div className="flex h-full flex-col">
      <ProjectStepper
        projectId={projectId}
        active="release"
        projectStatus={project.project_status}
        isProjectRedrafting={isProjectRedrafting}
      />
      <ProjectPageHeader project={project} activeView="grid" showViewToggle={false} />

      <div className="flex flex-1 items-start justify-center overflow-auto p-8">
        <Card className="w-full max-w-2xl">
          {showReReleaseBranch ? (
            !hasRedraftingInScope && !hasDraftInScope ? (
              <>
                <CardHeader>
                  <CardTitle>Nothing to re-release</CardTitle>
                  <CardDescription>
                    None of the selected rows are in Redrafting. Only Redrafting rows can be
                    re-released — Scheduled rows are already live, and In Process / Complete rows
                    are locked.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                    <dt className="text-muted-foreground">Scheduled (already live)</dt>
                    <dd>{scheduledCount}</dd>
                    <dt className="text-muted-foreground">In process (locked)</dt>
                    <dd>{inProgressCount}</dd>
                    <dt className="text-muted-foreground">Complete (locked)</dt>
                    <dd>{completeCount}</dd>
                  </dl>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" asChild>
                    <Link to="/projects/$projectId" params={{ projectId }}>
                      Back to project
                    </Link>
                  </Button>
                </CardFooter>
              </>
            ) : (
              <>
                <CardHeader>
                  <CardTitle>
                    {isSubsetScope
                      ? `Release ${redraftingIds.length} work order${redraftingIds.length === 1 ? '' : 's'}?`
                      : hasRedraftingInScope
                        ? 'Re-release redrafted work orders?'
                        : 'Release new work orders?'}
                  </CardTitle>
                  <CardDescription>
                    Work orders currently in Redrafting will flip back to Scheduled and pick up the
                    edits made during this redraft cycle. New work orders added during redrafting
                    will be released (Draft → Scheduled). In Process and Complete rows are
                    unaffected.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                    <dt className="text-muted-foreground">Project</dt>
                    <dd className="font-medium">{project.project_name}</dd>
                    {hasRedraftingInScope && (
                      <>
                        <dt className="text-muted-foreground">Will re-release</dt>
                        <dd>
                          {redraftingIds.length} redrafting work order
                          {redraftingIds.length === 1 ? '' : 's'}
                          {isSubsetScope ? ' from your selection' : ''}
                        </dd>
                      </>
                    )}
                    {hasDraftInScope && (
                      <>
                        <dt className="text-muted-foreground">New (Draft → Scheduled)</dt>
                        <dd>
                          {draftIds.length} new work order{draftIds.length === 1 ? '' : 's'}
                        </dd>
                      </>
                    )}
                    {scheduledCount > 0 && (
                      <>
                        <dt className="text-muted-foreground">Scheduled (unchanged)</dt>
                        <dd>{scheduledCount}</dd>
                      </>
                    )}
                    {inProgressCount > 0 && (
                      <>
                        <dt className="text-muted-foreground">In process (skipped)</dt>
                        <dd>{inProgressCount}</dd>
                      </>
                    )}
                    {completeCount > 0 && (
                      <>
                        <dt className="text-muted-foreground">Complete (skipped)</dt>
                        <dd>{completeCount}</dd>
                      </>
                    )}
                  </dl>
                  {missingFieldsByWo && (
                    <div className="border-destructive/30 bg-destructive/5 mt-4 rounded-md border p-3 text-sm">
                      <p className="text-destructive font-medium">
                        Populate these required fields before re-releasing:
                      </p>
                      <ul className="mt-2 space-y-1">
                        {missingFieldsByWo.map((row) => (
                          <li key={row.work_order_id} className="text-foreground">
                            <span className="font-mono text-xs">
                              {workOrderNameById.get(row.work_order_id) ?? row.work_order_id}
                            </span>
                            <span className="text-muted-foreground"> — </span>
                            <span>{row.missing.join(', ')}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
                <CardFooter className="flex justify-end gap-2">
                  <Button variant="outline" asChild>
                    <Link to="/projects/$projectId" params={{ projectId }}>
                      Cancel
                    </Link>
                  </Button>
                  <Button
                    onClick={() => void handleReRelease()}
                    disabled={!canReRelease || reRelease.isPending || releaseDrafts.isPending}
                  >
                    {reRelease.isPending || releaseDrafts.isPending
                      ? reRelease.progress
                        ? `Re-releasing ${reRelease.progress.done}/${reRelease.progress.total}…`
                        : 'Re-releasing…'
                      : isSubsetScope
                        ? `Release ${redraftingIds.length} work order${redraftingIds.length === 1 ? '' : 's'}`
                        : hasRedraftingInScope
                          ? 'Re-release work orders'
                          : 'Release new work orders'}
                  </Button>
                </CardFooter>
              </>
            )
          ) : isReleased ? (
            <>
              <CardHeader>
                <CardTitle>Project released</CardTitle>
                <CardDescription>
                  {project.released_at
                    ? `This project was released on ${formatDate(project.released_at)}.`
                    : 'This project has been released.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="secondary">released</Badge>
                  <span className="text-muted-foreground">
                    {workOrderCount} work order{workOrderCount === 1 ? '' : 's'}
                  </span>
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="outline" asChild>
                  <Link to="/projects/$projectId" params={{ projectId }}>
                    Back to project
                  </Link>
                </Button>
              </CardFooter>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle>Ready to release?</CardTitle>
                <CardDescription>
                  Once released, work orders will be locked for editing and assigned to the current
                  Bill of Operations. Every work order must have a Product Type selected.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Project</dt>
                  <dd className="font-medium">{project.project_name}</dd>
                  <dt className="text-muted-foreground">Status</dt>
                  <dd>
                    <Badge variant="default">{project.project_status}</Badge>
                  </dd>
                  <dt className="text-muted-foreground">Work orders</dt>
                  <dd>{workOrderCount}</dd>
                </dl>
                {missingFieldsByWo && (
                  <div className="border-destructive/30 bg-destructive/5 mt-4 rounded-md border p-3 text-sm">
                    <p className="text-destructive font-medium">
                      Populate these required fields before releasing:
                    </p>
                    <ul className="mt-2 space-y-1">
                      {missingFieldsByWo.map((row) => (
                        <li key={row.work_order_id} className="text-foreground">
                          <span className="font-mono text-xs">
                            {workOrderNameById.get(row.work_order_id) ?? row.work_order_id}
                          </span>
                          <span className="text-muted-foreground"> — </span>
                          <span>{row.missing.join(', ')}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button variant="outline" asChild>
                  <Link to="/projects/$projectId" params={{ projectId }}>
                    Cancel
                  </Link>
                </Button>
                <Button onClick={handleRelease} disabled={release.isPending}>
                  {release.isPending ? 'Releasing…' : 'Release Project'}
                </Button>
              </CardFooter>
            </>
          )}
        </Card>
      </div>

      {categoryGate === 'blocked' && (
        <ReleaseBlockedModal
          project={project}
          pending={release.isPending}
          onGoBack={() => {
            setCategoryGate(null);
            void navigate({ to: '/projects/$projectId', params: { projectId } });
          }}
          onApplyAllAndRelease={handleApplyAllAndRelease}
          onCancel={() => setCategoryGate(null)}
        />
      )}
      {categoryGate === 'zero_confirmed' && (
        <ReleaseWithoutCategoriesModal
          pending={release.isPending}
          onGoBack={() => setCategoryGate(null)}
          onReleaseWithout={() => runRelease('none')}
          onCancel={() => setCategoryGate(null)}
        />
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
